import axios from "axios";
import { isOnline } from "@/utils/connectivity";
import { hasFeature } from "@/utils/features";
import {
  cachePosSnapshot,
  getCachedPosSnapshot,
  idbDelete,
  idbGetAll,
  idbPut,
  toStorable,
} from "@/utils/posOffline/db";
import { getOrCreateDeviceUuid, generateUuid } from "@/utils/posOffline/device";
import { sameUsuarioOffline } from "@/utils/posOffline/cache";

const OP_ORDER = {
  layout_create: 10,
  layout_update: 20,
  layout_delete: 30,
  pedido_upsert: 40,
  liberar: 50,
  cobrar: 60,
  transferir: 55,
};

function notifyMesasOutboxChange() {
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event("mesas:outbox-updated"));
  }
}

export async function listOutboxMesas(usuario, statusFilter) {
  const all = (await idbGetAll("outbox_mesas")) || [];
  let rows = usuario
    ? all.filter((r) => sameUsuarioOffline(r.usuario, usuario))
    : all;
  if (statusFilter) {
    rows = rows.filter((r) => r.sync_status === statusFilter);
  }
  return rows.sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
}

export async function countPendingMesasOutbox(usuario) {
  const pending = await listOutboxMesas(usuario, "pending");
  const failed = await listOutboxMesas(usuario, "failed");
  const caja = await listOutboxMesas(usuario, "caja_mismatch");
  return pending.length + failed.length + caja.length;
}

/** Cobros/pedidos de mesa pendientes: 1 por mesa (no por cada op intermedia). */
export async function countPendingMesasCobrosPedidos(usuario) {
  const rows = await listOutboxMesas(usuario);
  const mesas = new Set();
  for (const r of rows) {
    if (!["pending", "failed", "caja_mismatch"].includes(r.sync_status)) continue;
    if (r.tipo !== "cobrar" && r.tipo !== "pedido_upsert" && r.tipo !== "liberar") continue;
    mesas.add(String(r.mesa_id));
  }
  return mesas.size;
}

export async function discardPendingMesaOps(usuario, mesaId, tipos = null) {
  if (mesaId == null) return [];
  const existing = await listOutboxMesas(usuario);
  const removed = [];
  for (const row of existing) {
    if (String(row.mesa_id) !== String(mesaId)) continue;
    if (!["pending", "failed", "caja_mismatch", "syncing"].includes(row.sync_status)) {
      continue;
    }
    if (tipos && !tipos.includes(row.tipo)) continue;
    removed.push(row);
    await idbDelete("outbox_mesas", row.client_mutation_id);
  }
  if (removed.length) notifyMesasOutboxChange();
  return removed;
}

/**
 * Encola una mutación de mesas.
 * - pedido_upsert: reemplaza pendientes de la misma mesa (reusa mutation id de sesión).
 * - cobrar: absorbe el último pedido_upsert (ítems finales) y descarta upsert/liberar previos.
 * - liberar: descarta upsert/cobrar pendientes de la mesa.
 */
export async function enqueueMesaOp({
  tipo,
  mesa_id,
  usuario,
  payload = {},
  client_mutation_id = null,
}) {
  const device_uuid = await getOrCreateDeviceUuid();
  let mutationId = client_mutation_id || null;
  let finalPayload = { ...(payload || {}) };

  if (tipo === "pedido_upsert" && mesa_id != null) {
    const existing = await listOutboxMesas(usuario);
    for (const row of existing) {
      if (
        String(row.mesa_id) !== String(mesa_id) ||
        !["pending", "failed", "caja_mismatch"].includes(row.sync_status)
      ) {
        continue;
      }
      if (row.tipo === "pedido_upsert") {
        if (!mutationId) mutationId = row.client_mutation_id;
        if (row.payload && row.payload.open_mutation_id && !finalPayload.open_mutation_id) {
          finalPayload.open_mutation_id = row.payload.open_mutation_id;
        }
        await idbDelete("outbox_mesas", row.client_mutation_id);
      }
      // Un cobro/liberar pendiente implica que la mesa ya se cerró localmente:
      // no tiene sentido seguir empujando un upsert.
      if (row.tipo === "cobrar" || row.tipo === "liberar") {
        await idbDelete("outbox_mesas", row.client_mutation_id);
      }
    }
    if (!finalPayload.open_mutation_id) {
      finalPayload.open_mutation_id = generateUuid();
    }
  }

  if (tipo === "cobrar" && mesa_id != null) {
    const removed = await discardPendingMesaOps(usuario, mesa_id, [
      "pedido_upsert",
      "liberar",
      "cobrar",
    ]);
    const prevUpsert = removed.find((r) => r.tipo === "pedido_upsert");
    if (prevUpsert && prevUpsert.payload) {
      if (finalPayload.items == null && prevUpsert.payload.items) {
        finalPayload.items = prevUpsert.payload.items;
      }
      if (finalPayload.vendedor == null && prevUpsert.payload.vendedor != null) {
        finalPayload.vendedor = prevUpsert.payload.vendedor;
      }
      if (!finalPayload.open_mutation_id && prevUpsert.payload.open_mutation_id) {
        finalPayload.open_mutation_id = prevUpsert.payload.open_mutation_id;
      }
    }
    if (!finalPayload.open_mutation_id) {
      finalPayload.open_mutation_id = generateUuid();
    }
  }

  if (tipo === "liberar" && mesa_id != null) {
    await discardPendingMesaOps(usuario, mesa_id, [
      "pedido_upsert",
      "liberar",
      "cobrar",
    ]);
  }

  if (!mutationId) mutationId = generateUuid();

  const entry = toStorable({
    client_mutation_id: mutationId,
    device_uuid,
    tipo,
    mesa_id: mesa_id != null ? Number(mesa_id) : null,
    usuario,
    payload: finalPayload,
    sync_status: "pending",
    created_at: new Date().toISOString(),
    last_error: null,
  });
  await idbPut("outbox_mesas", entry);
  notifyMesasOutboxChange();
  return entry;
}

export async function updateMesaOutboxEntry(id, patch) {
  const all = await idbGetAll("outbox_mesas");
  const row = all.find((x) => x.client_mutation_id === id);
  if (!row) return null;
  const next = toStorable({ ...row, ...patch });
  await idbPut("outbox_mesas", next);
  notifyMesasOutboxChange();
  return next;
}

export async function cacheMesasSalon(usuario, mesas) {
  if (!usuario) return;
  await cachePosSnapshot(`mesas:${usuario}`, Array.isArray(mesas) ? mesas : []);
}

export async function loadCachedMesasSalon(usuario) {
  const cached = await getCachedPosSnapshot(`mesas:${usuario}`);
  return Array.isArray(cached) ? cached : [];
}

export async function patchCachedMesa(usuario, mesaId, patch) {
  const mesas = await loadCachedMesasSalon(usuario);
  const idx = mesas.findIndex((m) => String(m.mesa_id) === String(mesaId));
  if (idx < 0) return mesas;
  mesas[idx] = { ...mesas[idx], ...patch };
  await cacheMesasSalon(usuario, mesas);
  return mesas;
}

function sortOpsForSync(ops) {
  return [...ops].sort((a, b) => {
    const oa = OP_ORDER[a.tipo] || 100;
    const ob = OP_ORDER[b.tipo] || 100;
    if (oa !== ob) return oa - ob;
    const mesaCmp = String(a.mesa_id ?? "").localeCompare(String(b.mesa_id ?? ""), "es", {
      numeric: true,
    });
    if (mesaCmp !== 0) return mesaCmp;
    return String(a.created_at || "").localeCompare(String(b.created_at || ""));
  });
}

async function remapMesaIdInOutbox(usuario, fromId, toId) {
  if (fromId == null || toId == null || String(fromId) === String(toId)) return;
  const all = await listOutboxMesas(usuario);
  for (const op of all) {
    if (String(op.mesa_id) !== String(fromId)) continue;
    if (op.tipo === "layout_create") continue;
    await updateMesaOutboxEntry(op.client_mutation_id, { mesa_id: Number(toId) });
  }
  const mesas = await loadCachedMesasSalon(usuario);
  const idx = mesas.findIndex((m) => String(m.mesa_id) === String(fromId));
  if (idx >= 0) {
    mesas[idx] = {
      ...mesas[idx],
      mesa_id: Number(toId),
      _offlineTemp: false,
    };
    await cacheMesasSalon(usuario, mesas);
  }
}

async function replayOp(op) {
  const mesaId = op.mesa_id;
  const payload = op.payload || {};

  switch (op.tipo) {
    case "layout_create": {
      const { data } = await axios.post("/mesas", payload);
      const newId = data && data.mesa_id;
      if (newId != null && mesaId != null && String(newId) !== String(mesaId)) {
        await remapMesaIdInOutbox(op.usuario, mesaId, newId);
        return { data, remappedFrom: mesaId, remappedTo: newId };
      }
      return { data };
    }
    case "layout_update": {
      if (mesaId != null && Number(mesaId) < 0) {
        throw new Error("La mesa local todavía no se sincronizó.");
      }
      const { data } = await axios.patch(`/mesas/${mesaId}`, payload);
      return data;
    }
    case "layout_delete": {
      if (mesaId != null && Number(mesaId) < 0) {
        return true;
      }
      await axios.delete(`/mesas/${mesaId}`);
      return true;
    }
    case "pedido_upsert": {
      if (mesaId != null && Number(mesaId) < 0) {
        throw new Error("La mesa local todavía no se sincronizó.");
      }
      const openMutationId = payload.open_mutation_id || op.client_mutation_id;
      await axios.post(`/mesas/${mesaId}/abrir`, {
        vendedor: payload.vendedor || null,
        client_mutation_id: openMutationId,
        device_uuid: op.device_uuid,
      });
      const { data } = await axios.patch(`/mesas/${mesaId}/pedido`, {
        items: payload.items || [],
        vendedor: payload.vendedor || null,
      });
      return data;
    }
    case "liberar": {
      if (mesaId != null && Number(mesaId) < 0) {
        return true;
      }
      const { data } = await axios.post(`/mesas/${mesaId}/liberar`, {
        client_mutation_id: op.client_mutation_id,
        device_uuid: op.device_uuid,
      });
      return data;
    }
    case "cobrar": {
      if (mesaId != null && Number(mesaId) < 0) {
        throw new Error("La mesa local todavía no se sincronizó.");
      }
      // Asegura pedido + ítems finales antes del cobro (el upsert previo se absorbió).
      const openMutationId = payload.open_mutation_id || `${op.client_mutation_id}:open`;
      await axios.post(`/mesas/${mesaId}/abrir`, {
        vendedor: payload.vendedor || null,
        client_mutation_id: openMutationId,
        device_uuid: op.device_uuid,
      });
      if (Array.isArray(payload.items)) {
        await axios.patch(`/mesas/${mesaId}/pedido`, {
          items: payload.items,
          vendedor: payload.vendedor || null,
        });
      }
      const { data } = await axios.post(`/mesas/${mesaId}/cobrar`, {
        medio_pago: payload.medio_pago,
        expected_caja_id: payload.expected_caja_id || null,
        client_mutation_id: op.client_mutation_id,
        device_uuid: op.device_uuid,
        resolve_caja_mismatch: payload.resolve_caja_mismatch || null,
        items_cobro: payload.items_cobro || null,
        cliente_id: payload.cliente_id || null,
        cliente_nombre: payload.cliente_nombre || null,
        descuento_pct: payload.descuento_pct || 0,
      });
      return data;
    }
    case "transferir": {
      if (mesaId != null && Number(mesaId) < 0) {
        throw new Error("La mesa local todavía no se sincronizó.");
      }
      const dest = payload.destino_mesa_id;
      if (dest != null && Number(dest) < 0) {
        throw new Error("La mesa destino todavía no se sincronizó.");
      }
      const { data } = await axios.post(`/mesas/${mesaId}/transferir`, {
        destino_mesa_id: dest,
        client_mutation_id: op.client_mutation_id,
        device_uuid: op.device_uuid,
      });
      return data;
    }
    default:
      throw new Error(`Tipo de operación de mesa desconocido: ${op.tipo}`);
  }
}

export async function syncOutboxMesas(usuario) {
  if (!isOnline() || !usuario) return { syncedCount: 0, conflicts: 0 };
  if (!hasFeature("mesas")) return { syncedCount: 0, conflicts: 0 };

  // Limpia colas viejas: si hay cobrar/liberar, no hace falta el upsert previo.
  const pre = await listOutboxMesas(usuario);
  const byMesa = new Map();
  for (const op of pre) {
    if (!["pending", "failed", "caja_mismatch"].includes(op.sync_status)) continue;
    const key = String(op.mesa_id);
    if (!byMesa.has(key)) byMesa.set(key, []);
    byMesa.get(key).push(op);
  }
  for (const [, ops] of byMesa) {
    const terminal = ops.find((o) => o.tipo === "cobrar" || o.tipo === "liberar");
    if (!terminal) continue;
    for (const op of ops) {
      if (op.tipo !== "pedido_upsert") continue;
      if (terminal.tipo === "cobrar") {
        const payload = { ...(terminal.payload || {}) };
        if (payload.items == null && op.payload && op.payload.items) {
          payload.items = op.payload.items;
        }
        if (payload.vendedor == null && op.payload && op.payload.vendedor != null) {
          payload.vendedor = op.payload.vendedor;
        }
        if (!payload.open_mutation_id && op.payload && op.payload.open_mutation_id) {
          payload.open_mutation_id = op.payload.open_mutation_id;
        }
        await updateMesaOutboxEntry(terminal.client_mutation_id, { payload });
      }
      await idbDelete("outbox_mesas", op.client_mutation_id);
    }
  }

  const queue = await listOutboxMesas(usuario);
  const pending = sortOpsForSync(
    queue.filter((op) =>
      ["pending", "failed", "caja_mismatch"].includes(op.sync_status),
    ),
  );

  let syncedCount = 0;
  let conflicts = 0;

  for (const op of pending) {
    try {
      await updateMesaOutboxEntry(op.client_mutation_id, {
        sync_status: "syncing",
        last_error: null,
      });
      const result = await replayOp(op);
      if (result && result.remappedFrom != null && result.remappedTo != null) {
        for (const later of pending) {
          if (String(later.mesa_id) === String(result.remappedFrom)) {
            later.mesa_id = Number(result.remappedTo);
          }
        }
      }
      await idbDelete("outbox_mesas", op.client_mutation_id);
      syncedCount++;
    } catch (e) {
      const data = e.response && e.response.data;
      const code = data && data.code;
      if (code === "CAJA_MISMATCH" || code === "SIN_CAJA_ABIERTA") {
        await updateMesaOutboxEntry(op.client_mutation_id, {
          sync_status: "caja_mismatch",
          last_error: (data && data.message) || code,
        });
        conflicts++;
      } else if (e.response && e.response.status === 401) {
        await updateMesaOutboxEntry(op.client_mutation_id, {
          sync_status: "failed",
          last_error: "auth_required",
        });
        conflicts++;
      } else {
        await updateMesaOutboxEntry(op.client_mutation_id, {
          sync_status: "failed",
          last_error: (data && data.message) || (e && e.message) || "Error",
        });
        conflicts++;
      }
    }
  }

  if (syncedCount > 0 || conflicts > 0) {
    try {
      const { data } = await axios.get("/mesas");
      if (Array.isArray(data)) {
        await cacheMesasSalon(usuario, data);
      }
    } catch (_) {
      /* ignore refresh errors */
    }
    notifyMesasOutboxChange();
  }

  return { syncedCount, conflicts };
}

/** Aplica items al snapshot local de una mesa (pedido offline). */
export function buildLocalPedidoResumen(mesa, items, vendedor) {
  const safeItems = Array.isArray(items) ? items : [];
  const total = Math.round(
    safeItems.reduce(
      (acc, it) =>
        acc +
        (Number(it.producto_precio) || 0) * (Number(it.cantidadSeleccionada) || 0),
      0,
    ) * 100,
  ) / 100;
  const cantidadItems = safeItems.reduce(
    (acc, it) => acc + (Number(it.cantidadSeleccionada) || 0),
    0,
  );
  const pedidoId =
    (mesa && mesa.pedido_actual_id) ||
    (mesa && mesa.pedido && mesa.pedido.pedido_id) ||
    `offline:${mesa && mesa.mesa_id}`;
  return {
    pedido_id: pedidoId,
    total,
    cantidad_items: cantidadItems,
    items: safeItems,
    estado: "abierto",
    medio_pago: "Pendiente de cobro",
    vendedor: vendedor || null,
    creado_en:
      (mesa && mesa.pedido && mesa.pedido.creado_en) || new Date().toISOString(),
    _offline: true,
  };
}

export function itemsDeltaStock(itemsAntes = [], itemsDespues = []) {
  const map = new Map();
  for (const it of itemsAntes || []) {
    if (!it?.producto_id) continue;
    const id = String(it.producto_id);
    map.set(id, (map.get(id) || 0) - (Number(it.cantidadSeleccionada) || 0));
  }
  for (const it of itemsDespues || []) {
    if (!it?.producto_id) continue;
    const id = String(it.producto_id);
    map.set(id, (map.get(id) || 0) + (Number(it.cantidadSeleccionada) || 0));
  }
  const cambios = [];
  for (const [producto_id, qtyNet] of map.entries()) {
    if (!qtyNet) continue;
    // qtyNet > 0 = más unidades en pedido → restar stock
    cambios.push({
      producto_id,
      delta: -qtyNet,
      producto_nombre:
        (itemsDespues || []).find((x) => String(x.producto_id) === producto_id)
          ?.producto_nombre ||
        (itemsAntes || []).find((x) => String(x.producto_id) === producto_id)
          ?.producto_nombre ||
        null,
    });
  }
  return cambios;
}
